import { isCertva, resolvePoKName } from './domainUtils.js';
import { POK_ORGANIZATION_SCHEMA, POK_PRODUCT_APPLICATIONS, POK_SOFTWARE, POK_URL, POK_WEBSITE } from './pokJsonLd.js';

/** The single place the white-label rule lives: certva must not claim POK's entity, so its pages
 * keep their title/description/canonical and drop every schema. That domain serves no prerendered
 * page — only the SPA shell — so everything under it renders in the browser, which is exactly where
 * `isCertva` can tell. At build time it is false, so pok.tech is unaffected. */
const whiteLabelSafe = (descriptors) => (isCertva ? descriptors.filter((d) => !('script:ld+json' in d)) : descriptors);

const definedGraphIds = (value, ids = []) => {
    if (Array.isArray(value)) value.forEach((item) => definedGraphIds(item, ids));
    else if (value && typeof value === 'object') {
        const id = value['@id'];
        if (id?.startsWith(`${POK_URL}/#`) && Object.keys(value).some((key) => key !== '@id')) ids.push(id);
        Object.values(value).forEach((item) => definedGraphIds(item, ids));
    }
    return ids;
};

const assertNoDuplicateEntities = (descriptors) => {
    const ids = definedGraphIds(descriptors.map((d) => d['script:ld+json']).filter(Boolean));
    const duplicate = ids.find((id, index) => ids.indexOf(id) !== index);
    if (duplicate) throw new Error(`JSON-LD defines ${duplicate} more than once on the same page`);
};

/** Every route's `meta()` returns through here: it adds the organization entity, so each page
 * carries it exactly once, and applies the white-label rule. A page that defines the same entity
 * twice fails the prerender; in the browser it is never checked, so it can never break a page. */
export const pokPageMeta = (descriptors) => {
    const withOrganization = [{ 'script:ld+json': POK_ORGANIZATION_SCHEMA }, ...descriptors];
    if (import.meta.env.SSR) assertNoDuplicateEntities(withOrganization);
    return whiteLabelSafe(withOrganization);
};

function stripHtml(text) {
    return text
        .replace(/<\/?(?:p|li|ul|ol|br|div|h[1-6])\b[^>]*>/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\s+/g, ' ')
        .trim();
}

const numericKeys = (obj) =>
    Object.keys(obj)
        .filter((key) => /^\d+$/.test(key))
        .map(Number)
        .sort((a, b) => a - b);

function keysOf(t, namespace) {
    const entries = t(namespace, { returnObjects: true });
    if (typeof entries !== 'object') throw new Error(`Missing translations for ${namespace}`);
    return numericKeys(entries);
}

/** Every question in the pool, so adding an FAQ does not also need a length bumped anywhere. */
export const allFaqs = (t) => keysOf(t, 'home.faqs');

/** POK's own feature list, shared by the features hub and by the `about` of each feature page so
 * neither drifts when a feature is added to the overview. */
export const buildPokFeatureList = (t) =>
    keysOf(t, 'features.overview').map((key) => t(`features.overview.${key}.title`));

export function buildBreadcrumbSchema(items, origin) {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: `${origin}${item.path}`,
        })),
    };
}

export function buildFaqSchema(questions, t, namespace = 'home.faqs') {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions.map((i) => ({
            '@type': 'Question',
            name: stripHtml(t(`${namespace}.${i}.q`, { POK_NAME: resolvePoKName() })),
            acceptedAnswer: {
                '@type': 'Answer',
                text: stripHtml(t(`${namespace}.${i}.a`, { POK_NAME: resolvePoKName() })),
            },
        })),
    };
}

export function buildFeaturePageSchema({ url, name, description, lang, datePublished, dateModified, t }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': url,
        name,
        url,
        description,
        inLanguage: lang,
        datePublished,
        dateModified,
        isPartOf: POK_WEBSITE,
        about: { ...POK_SOFTWARE, featureList: buildPokFeatureList(t) },
    };
}

export function buildProductPageSchema({ url, product, name, description, lang, datePublished, dateModified }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': url,
        name,
        url,
        description,
        inLanguage: lang,
        datePublished,
        dateModified,
        isPartOf: POK_WEBSITE,
        about: POK_PRODUCT_APPLICATIONS[product],
    };
}

export function buildFeaturesHubSchema(t) {
    return { '@context': 'https://schema.org', ...POK_SOFTWARE, featureList: buildPokFeatureList(t) };
}
